import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { usd, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Puzzle, Send, Loader2, Mail } from "lucide-react";
import { fmtDate } from "@/components/DatePicker";
import { toast } from "sonner";

function PitchDialog({ pitch, onClose, onSent }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [pitch]);
  if (!pitch) return null;
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/addon-pitch", { company_id: pitch.client.id, module: pitch.row.key, note }); toast.success(`Pitch emailed to ${data.to.join(", ")}`); onSent(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="addon-pitch-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> Pitch {pitch.row.label} to {pitch.client.name}</DialogTitle>
          <DialogDescription>Emails this client's HR manager(s) a tailored pitch: what {pitch.row.label} includes, their price ({usd(pitch.row.fee)}{pitch.row.per_employee ? " per employee" : ""}/mo) and a one-tap request link. Replies come to you.{pitch.client.last_pitch && <> Last pitched {fmtDate(pitch.client.last_pitch.slice(0, 10))}.</>}</DialogDescription></DialogHeader>
        <Textarea data-testid="addon-pitch-note" rows={3} placeholder="Optional personal note (e.g. “You mentioned handbook updates on our last call…”)" value={note} onChange={(e) => setNote(e.target.value)} />
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button data-testid="addon-pitch-send" disabled={busy} onClick={send}>{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send pitch</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddonRevenueCard() {
  const [d, setD] = useState(null);
  const [pitch, setPitch] = useState(null);
  const load = () => api.get("/billing/addon-revenue").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <Card className="p-4 sm:p-5" data-testid="addon-revenue-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div><h3 className="font-semibold font-display flex items-center gap-2"><Puzzle className="w-4 h-4 text-primary" /> Add-on revenue</h3><p className="text-xs text-muted-foreground">Monthly recurring from enabled add-ons · {d.total_clients} clients · tap a client name to email their HR a pitch</p></div>
        <div className="text-right"><div className="font-mono-num text-2xl font-bold text-emerald-600" data-testid="addon-total-mrr">{usd(d.total_mrr)}</div><div className="text-[11px] text-muted-foreground">per month</div></div>
      </div>
      <div className="space-y-3">
        {d.rows.map((r) => {
          const pct = d.total_clients ? Math.round((100 * r.clients) / d.total_clients) : 0;
          return (
            <div key={r.key} data-testid={`addon-row-${r.key}`}>
              <div className="flex items-center justify-between text-sm gap-2">
                <span className="font-medium truncate">{r.label}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap"><span data-testid={`addon-clients-${r.key}`}>{r.clients}/{r.total_clients}</span> clients · <span className="font-mono-num font-semibold text-foreground" data-testid={`addon-mrr-${r.key}`}>{usd(r.mrr)}</span>/mo</span>
              </div>
              <div className="h-1.5 rounded bg-muted overflow-hidden mt-1"><div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} /></div>
              <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-1">{usd(r.fee)}{r.per_employee ? " per employee" : " per client"}{r.without.length > 0 && <> · not on:{r.without.map((c) => <button key={c.id} type="button" data-testid={`addon-pitch-${r.key}-${c.id}`} title={c.last_pitch ? `Last pitched ${fmtDate(c.last_pitch.slice(0, 10))}` : "Email a pitch to this client's HR"} onClick={() => setPitch({ row: r, client: c })} className={`underline decoration-dotted hover:text-primary transition-colors ${c.last_pitch ? "text-emerald-700" : ""}`}>{c.name}</button>)}</>}</div>
            </div>
          );
        })}
      </div>
      <div className="text-xs mt-3 flex gap-3"><Link to="/clients" className="text-primary hover:underline" data-testid="addon-manage-link">Manage client modules</Link><Link to="/billing" className="text-primary hover:underline" data-testid="addon-pricing-link">Edit add-on pricing</Link></div>
      <PitchDialog pitch={pitch} onClose={() => setPitch(null)} onSent={load} />
    </Card>
  );
}
